import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '@contexts/AuthContext'
import { approvalService, type ApprovalDetail } from '@services/approvalService'
import { 
  ArrowLeft,
  User,
  Clock,
  DollarSign,
  Calendar,
  Star,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Mail,
  Briefcase,
  Trash2
} from 'lucide-react'
import toast from 'react-hot-toast'

const ApprovalDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [detail, setDetail] = useState<ApprovalDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [processing, setProcessing] = useState(false)
  const [qualityScore, setQualityScore] = useState(85)
  const [comments, setComments] = useState('')
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [rejectionReason, setRejectionReason] = useState('')
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleting, setDeleting] = useState(false)

  // 檢查權限
  if (!user || (user.role !== 'ADMIN' && user.role !== 'MANAGER')) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-secondary-800 mb-2">權限不足</h2>
          <p className="text-secondary-600">只有管理員和經理可以訪問審核功能</p>
        </div>
      </div>
    )
  }

  useEffect(() => {
    if (id) {
      loadDetail()
    }
  }, [id])

  const loadDetail = async () => {
    if (!id) return
    
    try {
      setLoading(true)
      const response = await approvalService.getApprovalDetail(id)
      if (response.success) {
        setDetail(response.data!)
      } else {
        toast.error('載入審核詳情失敗')
        navigate('/approvals')
      }
    } catch (error) {
      console.error('Load detail error:', error)
      toast.error('載入審核詳情失敗')
      navigate('/approvals')
    } finally {
      setLoading(false)
    }
  }

  const handleApprove = async () => {
    if (!id || !detail) return

    try {
      setProcessing(true)
      const response = await approvalService.approveTimesheet(id, {
        comments,
        qualityScore
      })

      if (response.success) {
        toast.success('工時申報已批准')
        navigate('/approvals')
      } else {
        toast.error('批准失敗')
      }
    } catch (error) {
      console.error('Approve error:', error)
      toast.error('批准失敗')
    } finally {
      setProcessing(false)
    }
  }

  const handleReject = async () => {
    if (!id || !rejectionReason.trim()) {
      toast.error('請填寫駁回原因')
      return
    }

    try {
      setProcessing(true)
      const response = await approvalService.rejectTimesheet(id, {
        rejectionReason: rejectionReason.trim()
      })

      if (response.success) {
        toast.success('工時申報已駁回')
        navigate('/approvals')
      } else {
        toast.error('駁回失敗')
      }
    } catch (error) {
      console.error('Reject error:', error)
      toast.error('駁回失敗')
    } finally {
      setProcessing(false)
      setShowRejectModal(false)
    }
  }

  const handleDelete = async () => {
    if (!id || !detail) return

    try {
      setDeleting(true)
      const response = await approvalService.deleteTimesheet(id)
      
      if (response.success) {
        toast.success('工時申報已刪除，相關工時已回復為未提交狀態')
        navigate('/approvals')
      } else {
        toast.error('刪除失敗')
      }
    } catch (error) {
      console.error('Delete error:', error)
      toast.error('刪除失敗')
    } finally {
      setDeleting(false)
      setShowDeleteModal(false)
    }
  }

  const setQuickScore = (score: number, comment: string) => {
    setQualityScore(score)
    setComments(comment)
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('zh-TW', {
      style: 'currency',
      currency: 'TWD',
      minimumFractionDigits: 0
    }).format(amount)
  }

  const formatDateRange = (start: string, end: string) => {
    const startDate = new Date(start).toLocaleDateString('zh-TW', { month: '2-digit', day: '2-digit' })
    const endDate = new Date(end).toLocaleDateString('zh-TW', { month: '2-digit', day: '2-digit' })
    return `${startDate} - ${endDate}`
  }

  const getHoursGradientClass = (hours: number) => {
    if (hours >= 10) return 'bg-blue-600 text-white'
    if (hours >= 8) return 'bg-blue-500 text-white'
    if (hours >= 6) return 'bg-blue-400 text-white'
    if (hours >= 4) return 'bg-blue-300 text-blue-900'
    if (hours >= 2) return 'bg-blue-200 text-blue-800'
    return 'bg-blue-100 text-blue-700'
  }

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      SUBMITTED: { label: '🔄 待審核', className: 'bg-yellow-100 text-yellow-800' },
      APPROVED: { label: '✅ 已批准', className: 'bg-green-100 text-green-800' },
      REJECTED: { label: '❌ 已駁回', className: 'bg-red-100 text-red-800' }
    }
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.SUBMITTED
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${config.className}`}>
        {config.label}
      </span>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (!detail) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-secondary-800 mb-2">找不到申報記錄</h2>
          <p className="text-secondary-600">請檢查申報ID是否正確</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* 頁面標題 */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/approvals')}
            className="btn-ghost flex items-center gap-2"
          >
            <ArrowLeft className="w-5 h-5" />
            返回列表
          </button>
          <div>
            <h1 className="text-2xl font-bold text-secondary-800">工時審核詳情</h1>
            <p className="text-secondary-600 mt-1">
              {detail.user?.name} - {formatDateRange(detail.weekStart, detail.weekEnd)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {getStatusBadge(detail.status)}
          {/* 刪除按鈕 - 只有管理員可以刪除已提交或已批准的申報 */}
          {user.role === 'ADMIN' && (detail.status === 'SUBMITTED' || detail.status === 'APPROVED') && (
            <button
              onClick={() => setShowDeleteModal(true)}
              className="inline-flex items-center px-3 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              title="刪除工時申報"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              刪除申報
            </button>
          )}
        </div>
      </div>

      {/* 員工基本資訊 - 響應式優化 */}
      <div className="bg-white rounded-xl shadow-soft p-4 md:p-6 border border-primary-200">
        <h2 className="text-lg font-semibold text-secondary-800 mb-4 flex items-center gap-2">
          <User className="w-5 h-5 text-primary-600" />
          員工基本資訊
        </h2>
        
        {/* 基本資訊 - 手機版2x2布局 */}
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
          <div>
            <p className="text-xs md:text-sm text-secondary-600 mb-1">員工姓名</p>
            <p className="font-medium text-secondary-800 text-sm md:text-base">{detail.user?.name}</p>
          </div>
          <div>
            <p className="text-xs md:text-sm text-secondary-600 mb-1">電子信箱</p>
            <p className="font-medium text-secondary-800 text-sm md:text-base flex items-center gap-1">
              <Mail className="w-3 h-3 md:w-4 md:h-4" />
              <span className="truncate">{detail.user?.email}</span>
            </p>
          </div>
          <div>
            <p className="text-xs md:text-sm text-secondary-600 mb-1">時薪</p>
            <p className="font-medium text-secondary-800 text-sm md:text-base">{formatCurrency(detail.user?.hourlyRate || 0)}/小時</p>
          </div>
          <div>
            <p className="text-xs md:text-sm text-secondary-600 mb-1">提交時間</p>
            <p className="font-medium text-secondary-800 text-sm md:text-base">
              {detail.submittedAt ? new Date(detail.submittedAt).toLocaleString('zh-TW') : '-'}
            </p>
          </div>
        </div>
        
        {/* 統計數據 - 手機版垂直堆疊 */}
        <div className="grid grid-cols-3 gap-3 md:gap-6 mt-4 md:mt-6 pt-4 md:pt-6 border-t border-secondary-200">
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 md:gap-2 mb-1 md:mb-2">
              <Clock className="w-4 h-4 md:w-5 md:h-5 text-blue-500" />
              <span className="text-xs md:text-sm text-secondary-600">總工時</span>
            </div>
            <p className="text-lg md:text-2xl font-bold text-secondary-800">{detail.totalHours.toFixed(2)}h</p>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 md:gap-2 mb-1 md:mb-2">
              <DollarSign className="w-4 h-4 md:w-5 md:h-5 text-green-500" />
              <span className="text-xs md:text-sm text-secondary-600">總金額</span>
            </div>
            <p className="text-lg md:text-2xl font-bold text-secondary-800">{formatCurrency(detail.totalAmount)}</p>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 md:gap-2 mb-1 md:mb-2">
              <Calendar className="w-4 h-4 md:w-5 md:h-5 text-purple-500" />
              <span className="text-xs md:text-sm text-secondary-600">工作日數</span>
            </div>
            <p className="text-lg md:text-2xl font-bold text-secondary-800">{detail.workingDays}天</p>
          </div>
        </div>
      </div>

      {/* 工時分析圖表 - 響應式設計 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 每日工時分布 */}
        <div className="bg-white rounded-xl shadow-soft p-4 md:p-6 border border-primary-200">
          <h3 className="text-lg font-semibold text-secondary-800 mb-4">每日工時分布</h3>
          
          {/* 桌面版圖表（保持原樣） */}
          <div className="hidden md:block space-y-3">
            {Object.entries(detail.dailyHours).map(([date, hours]) => (
              <div key={date} className="flex items-center justify-between">
                <span className="text-sm text-secondary-600">
                  {new Date(date).toLocaleDateString('zh-TW', { weekday: 'short', month: '2-digit', day: '2-digit' })}
                </span>
                <div className="flex items-center gap-2">
                  <div className="w-32 bg-secondary-200 rounded-full h-2">
                    <div 
                      className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${Math.min((hours / 12) * 100, 100)}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium text-secondary-800 w-12 text-right">
                    {hours.toFixed(2)}h
                  </span>
                </div>
              </div>
            ))}
          </div>
          
          {/* 手機版進度條 */}
          <div className="md:hidden space-y-3">
            {Object.entries(detail.dailyHours).map(([date, hours]) => (
              <div key={date} className="flex items-center justify-between">
                <span className="text-sm text-secondary-600 w-12">
                  {new Date(date).toLocaleDateString('zh-TW', { weekday: 'short' })}
                </span>
                <div className="flex items-center gap-2 flex-1">
                  <div className="flex-1 bg-secondary-200 rounded-full h-2 mx-2">
                    <div 
                      className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${Math.min((hours / 12) * 100, 100)}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium text-secondary-800 w-12 text-right">
                    {hours.toFixed(1)}h
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 專案時間分配 */}
        <div className="bg-white rounded-xl shadow-soft p-4 md:p-6 border border-primary-200">
          <h3 className="text-lg font-semibold text-secondary-800 mb-4">專案時間分配</h3>
          
          {/* 桌面版圓餅圖樣式（保持原樣） */}
          <div className="hidden md:block space-y-3">
            {Object.entries(detail.projectHours).map(([projectId, project]) => (
              <div key={projectId} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div 
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: project.color }}
                  />
                  <span className="text-sm text-secondary-700">{project.name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-secondary-600">
                    {((project.hours / detail.totalHours) * 100).toFixed(1)}%
                  </span>
                  <span className="text-sm font-medium text-secondary-800 w-12 text-right">
                    {project.hours.toFixed(2)}h
                  </span>
                </div>
              </div>
            ))}
          </div>
          
          {/* 手機版列表 */}
          <div className="md:hidden space-y-3">
            {Object.entries(detail.projectHours).map(([projectId, project]) => (
              <div key={projectId} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div 
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: project.color }}
                  />
                  <span className="text-sm text-secondary-700">{project.name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-secondary-600">
                    {((project.hours / detail.totalHours) * 100).toFixed(1)}%
                  </span>
                  <span className="text-sm font-medium text-secondary-800 w-12 text-right">
                    {project.hours.toFixed(1)}h
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 詳細工時記錄 - 響應式設計 */}
      <div className="bg-white rounded-xl shadow-soft border border-primary-200">
        <div className="px-4 md:px-6 py-3 md:py-4 border-b border-secondary-200">
          <h3 className="text-lg font-semibold text-secondary-800">詳細工時記錄</h3>
        </div>
        
        {/* 桌面版表格 */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-secondary-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">日期</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">專案</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">時間範圍</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">工時</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">金額</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">工作描述</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-secondary-200">
              {Object.entries(detail.entriesByDate).map(([date, entries]) => (
                <React.Fragment key={date}>
                  {entries.map((entry: any, index: number) => (
                    <tr key={entry.id} className="hover:bg-secondary-50">
                      {index === 0 && (
                        <td 
                          className="px-6 py-4 whitespace-nowrap text-sm font-medium text-secondary-900 border-r border-secondary-200"
                          rowSpan={entries.length}
                        >
                          {new Date(date).toLocaleDateString('zh-TW', { 
                            month: '2-digit', 
                            day: '2-digit',
                            weekday: 'short'
                          })}
                        </td>
                      )}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: entry.project.color }}
                          />
                          <span className="text-sm text-secondary-900">{entry.project.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-900">
                        {new Date(entry.startTime).toLocaleTimeString('zh-TW', { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })} - {entry.endTime ? new Date(entry.endTime).toLocaleTimeString('zh-TW', { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        }) : '進行中'}
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium rounded ${getHoursGradientClass(entry.hours)}`}>
                        {entry.hours}h
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-900">
                        {formatCurrency(entry.amount)}
                      </td>
                      <td className="px-6 py-4 text-sm text-secondary-700 max-w-xs truncate">
                        {entry.description || '-'}
                      </td>
                    </tr>
                  ))}
                  {/* 日總計行 */}
                  <tr className="bg-secondary-100 font-medium">
                    <td className="px-6 py-2 text-sm text-secondary-800" colSpan={3}>
                      日總計
                    </td>
                    <td className="px-6 py-2 text-sm font-bold text-secondary-900">
                      {detail.dailyHours[date]?.toFixed(2)}h
                    </td>
                    <td className="px-6 py-2 text-sm font-bold text-secondary-900">
                      {formatCurrency(entries.reduce((sum: number, entry: any) => sum + entry.amount, 0))}
                    </td>
                    <td className="px-6 py-2"></td>
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        {/* 手機版卡片 */}
        <div className="md:hidden p-4 space-y-4">
          {Object.entries(detail.entriesByDate).map(([date, entries]) => (
            <div key={date} className="space-y-3">
              {/* 日期標題 - 合併總計顯示 */}
              <div className="flex items-center justify-between py-2 bg-secondary-50 rounded-lg px-3">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-primary-600" />
                  <span className="font-medium text-secondary-800 text-sm">
                    {new Date(date).toLocaleDateString('zh-TW', { 
                      month: '2-digit', 
                      day: '2-digit',
                      weekday: 'short'
                    })}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xs font-medium text-primary-700">
                  <span>{detail.dailyHours[date]?.toFixed(1)}h</span>
                  <span>•</span>
                  <span>{formatCurrency(entries.reduce((sum: number, entry: any) => sum + entry.amount, 0))}</span>
                </div>
              </div>

              {/* 該日的工時記錄 */}
              <div className="space-y-2 pl-0">
                {entries.map((entry: any) => (
                  <div key={entry.id} className="bg-secondary-50 rounded-lg p-3 border border-secondary-200">
                    {/* 專案和時間行 */}
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: entry.project.color }}
                        />
                        <span className="font-medium text-secondary-800 text-sm">
                          {entry.project.name}
                        </span>
                      </div>
                      <span className="text-xs text-secondary-600">
                        {new Date(entry.startTime).toLocaleTimeString('zh-TW', { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })} - {entry.endTime ? new Date(entry.endTime).toLocaleTimeString('zh-TW', { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        }) : '進行中'}
                      </span>
                    </div>

                    {/* 工時和金額行 */}
                    <div className="flex items-center gap-4 mb-2">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getHoursGradientClass(entry.hours)}`}>
                        {entry.hours}h
                      </span>
                      <span className="text-sm text-secondary-700">
                        {formatCurrency(entry.amount)}
                      </span>
                    </div>

                    {/* 工作描述 */}
                    {entry.description && (
                      <div className="pt-2 border-t border-secondary-300">
                        <p className="text-sm text-secondary-700 leading-relaxed">
                          {entry.description}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 員工申報說明 */}
      {detail.comments && (
        <div className="bg-white rounded-xl shadow-soft p-6 border border-primary-200">
          <h3 className="text-lg font-semibold text-secondary-800 mb-4 flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-primary-600" />
            員工申報說明
          </h3>
          <div className="bg-secondary-50 rounded-lg p-4">
            <p className="text-secondary-700 whitespace-pre-wrap">{detail.comments}</p>
          </div>
        </div>
      )}

      {/* 審核決定區域 - 手機版優化 */}
      {detail.status === 'SUBMITTED' && (
        <div className="bg-white rounded-xl shadow-soft p-4 md:p-6 border border-primary-200">
          <h3 className="text-lg font-semibold text-secondary-800 mb-6 flex items-center gap-2">
            <Star className="w-5 h-5 text-primary-600" />
            審核決定
          </h3>

          {/* 品質評分 - 手機版優化 */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-secondary-700 mb-3">
              品質評分 ({qualityScore}分)
            </label>
            
            {/* 星級顯示 */}
            <div className="flex items-center justify-center gap-1 mb-4">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className={`w-6 h-6 md:w-8 md:h-8 ${
                    i < Math.floor(qualityScore / 20) ? 'text-yellow-400 fill-current' : 'text-secondary-300'
                  }`}
                />
              ))}
            </div>
            
            {/* 滑桿 */}
            <div className="mb-4">
              <input
                type="range"
                min="70"
                max="100"
                value={qualityScore}
                onChange={(e) => setQualityScore(parseInt(e.target.value))}
                className="w-full h-2 bg-secondary-200 rounded-lg appearance-none cursor-pointer"
              />
              <div className="flex justify-between text-xs text-secondary-500 mt-1">
                <span>70</span>
                <span>80</span>
                <span>90</span>
                <span>100</span>
              </div>
            </div>
            
            {/* 快速設定按鈕 - 手機版2x2布局 */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <button
                onClick={() => setQuickScore(95, '工時記錄完整準確，工作描述詳細清楚，專案分配合理，整體表現傑出。感謝您的專業態度和高品質工作。')}
                className="btn-ghost text-xs px-3 py-2"
              >
                95分
              </button>
              <button
                onClick={() => setQuickScore(90, '工時記錄完整，工作描述清楚，專案執行良好，整體表現優秀。請繼續保持這樣的工作品質。')}
                className="btn-ghost text-xs px-3 py-2"
              >
                90分
              </button>
              <button
                onClick={() => setQuickScore(85, '工時記錄基本完整，工作描述清楚，整體表現符合預期。建議可以更詳細記錄工作內容以提升品質。')}
                className="btn-ghost text-xs px-3 py-2"
              >
                85分
              </button>
              <button
                onClick={() => setQuickScore(80, '工時記錄基本符合要求，但部分工作描述可以更詳細。建議加強工時記錄的完整性和準確性。')}
                className="btn-ghost text-xs px-3 py-2"
              >
                80分
              </button>
            </div>
          </div>

          {/* 審核意見 */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              審核意見
            </label>
            <textarea
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder="請填寫審核意見..."
              rows={3}
              className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* 操作按鈕 - 手機版優化 */}
          <div className="flex flex-col md:flex-row gap-3 justify-end">
            <button
              onClick={handleApprove}
              disabled={processing}
              className="w-full md:w-auto btn-primary flex items-center justify-center gap-2"
            >
              <CheckCircle className="w-5 h-5" />
              {processing ? '處理中...' : '批准申報'}
            </button>
            <button
              onClick={() => setShowRejectModal(true)}
              disabled={processing}
              className="w-full md:w-auto btn-danger flex items-center justify-center gap-2"
            >
              <XCircle className="w-5 h-5" />
              駁回申報
            </button>
          </div>
        </div>
      )}


      {/* 駁回確認彈窗 */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center gap-3 mb-4">
              <XCircle className="w-6 h-6 text-red-500" />
              <h3 className="text-lg font-semibold text-secondary-800">駁回工時申報</h3>
            </div>
            
            <div className="mb-4">
              <p className="text-sm text-secondary-600 mb-2">
                {detail.user?.name} ({formatDateRange(detail.weekStart, detail.weekEnd)}) - {detail.totalHours}小時 - {formatCurrency(detail.totalAmount)}
              </p>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                駁回原因 (必填)
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="請詳細說明駁回原因，以便員工了解需要修改的地方..."
                rows={4}
                className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowRejectModal(false)}
                disabled={processing}
                className="btn-ghost"
              >
                取消
              </button>
              <button
                onClick={handleReject}
                disabled={processing || !rejectionReason.trim()}
                className="btn-danger"
              >
                {processing ? '處理中...' : '確認駁回'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 刪除確認彈窗 */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center gap-3 mb-4">
              <Trash2 className="w-6 h-6 text-red-500" />
              <h3 className="text-lg font-semibold text-secondary-800">刪除工時申報</h3>
            </div>
            
            <div className="mb-4">
              <p className="text-sm text-secondary-600 mb-2">
                您確定要刪除以下工時申報嗎？
              </p>
              <div className="bg-secondary-50 rounded-lg p-3">
                <p className="text-sm"><strong>員工：</strong>{detail.user?.name}</p>
                <p className="text-sm"><strong>週期：</strong>{formatDateRange(detail.weekStart, detail.weekEnd)}</p>
                <p className="text-sm"><strong>工時：</strong>{detail.totalHours}h</p>
                <p className="text-sm"><strong>金額：</strong>{formatCurrency(detail.totalAmount)}</p>
                <p className="text-sm"><strong>狀態：</strong>{getStatusBadge(detail.status)}</p>
              </div>
            </div>

            <div className="mb-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4 text-yellow-600" />
                  <span className="text-sm font-medium text-yellow-800">注意事項</span>
                </div>
                <ul className="text-xs text-yellow-700 space-y-1">
                  <li>• 刪除後，相關的工時記錄將回復為「未提交」狀態</li>
                  <li>• 員工可以重新修改和提交這些工時</li>
                  <li>• 此操作無法撤銷</li>
                </ul>
              </div>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-secondary-700 bg-white border border-secondary-300 rounded-md hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
              >
                取消
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
              >
                {deleting ? '刪除中...' : '確認刪除'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ApprovalDetailPage
